import Button from '@/shared/Button';
import Div from '@/shared/Div';
import Heading from '@/shared/Heading';
import Paragraph from '@/shared/Paragraph';
import React, { memo, useState } from 'react';
import ExchangeOldProduct from './ExchangeOldProduct';
import ExchangeLearnMore from './ExchangeLearnMore';
import { getFormattedPrice } from '@/utils/price';
import ExchangeIcon from '@/components/Icons/Exchange';
import Discount from '@/components/Icons/Discount';
import { useEcomProductContext } from '@/context/ecomProductContext';
import useEcomProduct from '@/hooks/useEcomProduct';
import Drawer from '@/shared/crisp/Drawer';
import useEcomGTM from '@/hooks/useEcomGTM';
import { EcomGTMEventNames } from '@/types/ecomGTM';
import { EXISTING_PREXO_DETAILS, PREXO_MAPPING, PREXO_PRODUCT_NAME } from '@/constants/app';
import Tag from '@/shared/crisp/Tag';
import Span from '@/shared/Span';
import LocalStorage from '@/utils/localStorage';
import classNames from 'classnames';
import { useCartActions } from '@/hooks/useCartActions';
import ImageWithFallback from '@/globalComponents/image';
import StarIcon from '../Icons/StarIcon';
import Truck from '../Icons/Truck';
import VerifiedIcon from '../Icons/VerifiedIcon';
import PrexoImage from 'public/images/MultipleDevicePrexo.png';

export const prexoLearnMoreData = {
  heading: 'Learn how exchange works',
  description: 'Get flat discounts on a new water purifier, by exchanging ANY old appliance.',
  link: {
    label: 'FAQs',
    href: '/faqs-product-exchange-offer',
    target: '_blank',
  },
  list: [
    { Icon: <Discount />, title: 'Get Instant Discounts' },
    { Icon: <VerifiedIcon />, title: 'Any Condition Accepted' },
    { Icon: <Truck />, title: 'Free Doorstep Pickup' },
  ],
  stepsList: [
    { title: 'Make sure your product is pick-up ready.' },
    {
      title: 'Your old item will be verified at the doorstep by our delivery agent.',
    },
    {
      title: 'If there is a product mis-match, your order will be cancelled.',
    },
  ],
  infoStepsList: [
    {
      title: 'Make your product pick-up ready',
      subTitle: 'This means all accessories are unplugged, and the unit is prepared for handover.',
    },
    {
      title: 'Your product will be verified',
      subTitle: 'Your old item will be verified at the doorstep by our delivery agent.',
    },
    {
      title: 'Exchange will be initiated',
      subTitle:
        'You can hand in your existing product to the delivery agent, and receive the new one.',
    },
  ],
};

function ExchangeProduct(props: any) {
  const [modalOpen, setModalOpen] = useState(false);
  const [learnMore, setLearnMore] = useState(false);
  const { dispatch: ecomProductDispatch } = useEcomProductContext();
  const {
    price = {},
    productId,
    materialId,
    isProductInCart,
    isProductOutOfStock,
    flags,
    category,
    handleAddToCart,
    selectedPrexo,
    setSelectedPrexo,
  } = props;
  const product = useEcomProduct(materialId);
  const { fireEcomGTMEvent } = useEcomGTM();
  const { removeItemfn } = useCartActions();
  const onConfirm = () => {
    ecomProductDispatch.updatePrexo({ materialId, prexo: price.prexo });
  };

  const onClose = () => {
    setModalOpen(false);
  };

  if (!price?.prexo || !flags?.prexoApplicable || !price.prexo.eligible) return null;

  const prexoDiscount = getFormattedPrice(price?.prexo?.prexoDisc);
  const prexoValues = price?.prexo?.prexoValues || [];
  const maxPrexoValue =
    prexoValues.length > 0
      ? Math.max(...prexoValues.map((item: any) => item.value))
      : price?.prexo?.prexoDisc || 0;

  const prexoExchangeDetails = LocalStorage.get(EXISTING_PREXO_DETAILS) || {};
  const allPrexoEntries = LocalStorage.get(PREXO_MAPPING) || [];

  const targetMaterialId = materialId;

  const productDetails =
    allPrexoEntries.find(
      (entry: { materialId: string }) => entry.materialId === targetMaterialId,
    ) || '';

  const prexoExchangeCategoryName = prexoExchangeDetails.prexoExchangeCategoryName;
  const prexoExchangeMRP = prexoExchangeDetails.prexoExchangeMRP;

  prexoLearnMoreData.description = prexoLearnMoreData.description.replace(
    '{disc}',
    String(maxPrexoValue),
  );

  const choosePrexoClick = () => {
    setModalOpen(true);
    fireEcomGTMEvent(EcomGTMEventNames.PDP.PREXO_SELECTION_STARTED, {});
  };

  const removePrexoClick = () => {
    if (isProductInCart) {
      removeItemfn(productId, price?.prexo?.id, 'prexo', materialId);
    }
    ecomProductDispatch.updatePrexo({ materialId, prexo: null });
    setSelectedPrexo?.(null);
    LocalStorage.remove('EXISTING_PREXO_DETAILS');
    const existingEntries = LocalStorage.get(PREXO_MAPPING) || [];
    const updatedEntries = existingEntries.filter(
      (entry: { materialId: string }) => entry.materialId !== materialId,
    );
    LocalStorage.set(PREXO_MAPPING, updatedEntries);
  };
  const categoryType = PREXO_PRODUCT_NAME[category?.type];

  return (
    <>
      <Div className="flex flex-col mt-s9 md:mt-s10">
        <Heading level={3} className="text-h-3 text-cti-primary lg:text-hd-3">
          Ways to buy
        </Heading>
        <Div className="mt-s5 md:mt-s5  flex rounded-cm border border-[#E5BDFF]  bg-prexo-gradient p-s5 px-s5 py-5 pb-s2">
          <Div className="flex-grow">
            <Tag
              background="bg-extended-efbrand"
              className="text-body-xs absolute right-4 lg:right-10 z-0 !rounded-none !rounded-bl-cxxs !rounded-tr-cs !mb-s3 -mt-[1.3rem] pb-s2 inline-flex items-center gap-s2"
            >
              <StarIcon />
              WEB EXCLUSIVE
            </Tag>
            <ImageWithFallback src={PrexoImage} alt="Multiple devices image" height={100} />
            {product.prexo || productDetails?.materialId === materialId ? (
              <>
                <Div className="flex items-center justify-between gap-s4 mt-s2 mb-s2">
                  <Div>
                    <Paragraph className="text-h-4 md:text-hd-4 text-cti-primary mb-s2">
                      Exchange Discount Applied
                    </Paragraph>
                    <Paragraph className="text-body-sm md:text-body-sm-desktop text-cti-secondary">
                      {`For your ${
                        productDetails?.materialId === materialId
                          ? productDetails.prexoExchangeCategoryName
                          : selectedPrexo?.categoryName ?? prexoExchangeCategoryName
                      }`}
                    </Paragraph>
                  </Div>

                  {!isProductOutOfStock && (
                    <Button
                      theme={'secondary'}
                      size="sm"
                      className="py-s3 px-s5 h-fit rounded-cxs !body-base !text-button-normal bg-transparent"
                      onClick={removePrexoClick}
                      dataTestId="choose-id"
                    >
                      {'Remove'}
                    </Button>
                  )}
                </Div>
              </>
            ) : (
              <>
                <Div className="flex justify-between items-center gap-s4">
                  <Paragraph className="text-h-4 md:text-hd-4 text-cti-primary mt-s2">
                    Exchange ANY old appliance <br /> for discounts on a new device!
                  </Paragraph>

                  {!isProductOutOfStock && (
                    <Button
                      theme={'primary'}
                      size="sm"
                      className={classNames(
                        'py-s3 px-s5 h-fit rounded-cxs !body-base !text-button-normal',
                      )}
                      onClick={choosePrexoClick}
                      dataTestId="choose-id"
                    >
                      {'Select'}
                    </Button>
                  )}
                </Div>
              </>
            )}

            <Div className="flex justify-between items-center">
              <Paragraph className="text-callout-small md:text-callout-normal text-cu-positive mt-2">
                {product.prexo || productDetails?.materialId === materialId ? (
                  <Div className="flex items-center gap-s2">
                    <ExchangeIcon className="mt-s1" />
                    <Span className="text-center">
                      Flat ₹
                      {productDetails?.materialId === materialId
                        ? productDetails.prexoExchangeMRP
                        : selectedPrexo?.value ?? prexoExchangeMRP}{' '}
                      OFF
                    </Span>
                  </Div>
                ) : (
                  'Get upto ₹' + maxPrexoValue + ' OFF'
                )}
              </Paragraph>
              <Button
                theme="link"
                dataTestId="learn-more"
                className="text-button-small"
                onClick={() => {
                  setModalOpen(true);
                  setLearnMore(true);
                }}
              >
                Learn more
              </Button>
            </Div>
          </Div>
        </Div>
      </Div>
      {modalOpen && (
        <Drawer
          modalOpen={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setLearnMore(false);
          }}
        >
          {learnMore ? (
            <ExchangeLearnMore data={prexoLearnMoreData} categoryType={categoryType} />
          ) : (
            <ExchangeOldProduct
              onConfirm={onConfirm}
              productId={productId}
              onClose={onClose}
              prexoDiscount={prexoDiscount}
              prexoValues={prexoValues}
              categoryType={categoryType}
              handleAddToCart={handleAddToCart}
              onSelectPrexoName={setSelectedPrexo}
            />
          )}
        </Drawer>
      )}
    </>
  );
}

export default memo(ExchangeProduct);
